import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteUser, suspendUser, unsuspendUser } from '../../api/users';

export type PendingAction = 'suspend' | 'unsuspend' | 'delete';

export interface UserActionMutations {
  suspend: (id: string, reason: string) => void;
  unsuspend: (id: string, reason: string) => void;
  delete: (id: string, reason: string) => void;
  suspendAsync: (id: string, reason: string) => Promise<{ ok: true }>;
  unsuspendAsync: (id: string, reason: string) => Promise<{ ok: true }>;
  deleteAsync: (id: string, reason: string) => Promise<{ ok: true }>;
  pendingActions: Record<string, PendingAction | null>;
}

export function useUserActionMutations(): UserActionMutations {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
  // #583 — onError is deliberately NOT on the useMutation options.
  // If it were, mutateAsync's rejection would still fire it once per
  // failed row on top of the bulk handler's own summary alert (double
  // modal for the exact halt path Adam operates on). Instead, per-row
  // `.mutate()` callers pass a per-call onError; `.mutateAsync()` used
  // by the bulk loop rejects silently and only the bulk handler alerts.
  const suspendMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => suspendUser(id, reason),
    onSuccess: invalidate,
  });
  const unsuspendMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => unsuspendUser(id, reason),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => deleteUser(id, reason),
    onSuccess: invalidate,
  });
  const pendingActions: Record<string, PendingAction | null> = {};
  if (suspendMut.isPending && suspendMut.variables) pendingActions[suspendMut.variables.id] = 'suspend';
  if (unsuspendMut.isPending && unsuspendMut.variables) pendingActions[unsuspendMut.variables.id] = 'unsuspend';
  if (deleteMut.isPending && deleteMut.variables) pendingActions[deleteMut.variables.id] = 'delete';
  return {
    suspend: (id, reason) =>
      suspendMut.mutate(
        { id, reason },
        { onError: (err: Error) => window.alert(`Suspend failed: ${err.message}`) },
      ),
    unsuspend: (id, reason) =>
      unsuspendMut.mutate(
        { id, reason },
        { onError: (err: Error) => window.alert(`Unsuspend failed: ${err.message}`) },
      ),
    delete: (id, reason) =>
      deleteMut.mutate(
        { id, reason },
        { onError: (err: Error) => window.alert(`Delete failed: ${err.message}`) },
      ),
    // Async variants for the bulk-actions bar (#583) — sequential loop
    // needs to await each individual result to short-circuit on the first
    // failure. No onError attached; the bulk handler owns the alert.
    suspendAsync: (id, reason) => suspendMut.mutateAsync({ id, reason }),
    unsuspendAsync: (id, reason) => unsuspendMut.mutateAsync({ id, reason }),
    deleteAsync: (id, reason) => deleteMut.mutateAsync({ id, reason }),
    pendingActions,
  };
}
